<template>
  <div class="productConfigurator">
    <v-alert v-if="prefillUnavailable" type="error" text>{{ $t('UPSELL_SOURCE_PERSON_UNAVAILABLE') }}</v-alert>
    <BasicConfigurator v-else-if="product.imageRequirements.length === 0" :value="[[]]" :product="product" :configuration-valid="true" @addedToBasket="onAddedToBasket" @error="onError"/>
    <SetConfigurator v-else-if="isSet" v-model="selectedImages" :product="product" :prefill="prefill" @addedToBasket="onAddedToBasket" @error="onError" @backToProducts="$emit('backToProducts')"/>
    <ImageBatchSelector v-else-if="needsBatchSelection" v-model="selectedImages" :product="product" :prefill="prefill" @addedToBasket="onAddedToBasket" @error="onError" @backToProducts="$emit('backToProducts')"/>
    <SingleConfigurator v-else v-model="selectedImages" :product="product" :prefill="prefill" @addedToBasket="onAddedToBasket" @error="onError" @backToProducts="$emit('backToProducts')"/>
  </div>

</template>

<script lang="ts">
import Component from 'vue-class-component'
import BasicComponent from "@/global/BasicComponent";
import {Prop} from "vue-property-decorator";
import {ConfiguredImageDto, ImageFilterDto, ImageRequirementDto, ProductDto, ProductTypeDto} from "@swagger";
import ImageBatchSelector from "@/components/product-configuration/ImageBatchSelector.vue";
import PersonSelector from "@/components/product-configuration/PersonSelector.vue";
import SetConfigurator from "@/components/product-configuration/SetConfigurator.vue";
import BasicConfigurator from "@/components/product-configuration/BasicConfigurator.vue";
import SingleConfigurator from "@/components/product-configuration/SingleConfigurator.vue";
import TypeEnum = ImageRequirementDto.TypeEnum;
import {mapConfigurationPrefill, ProductConfigurationPrefill} from '@/global/ProductConfigurationPrefill';

@Component({
  components: {
    BasicConfigurator,
    SingleConfigurator,
    ImageBatchSelector,
    SetConfigurator,
    PersonSelector,
    ImageSelectionRow: ImageBatchSelector
  }
})
export default class ProductConfigurator extends BasicComponent {

  @Prop(Object) product: ProductDto;
  @Prop(Object) prefill: ProductConfigurationPrefill;

  selectedImages: ConfiguredImageDto[] = [];

  get prefillUnavailable(): boolean {
    return !!this.prefill && !this.allPersons.some(person => this.prefill.personCodes.includes(person.code)
      && person.productIds.includes(this.product.id) && this.product.imageRequirements.every(requirement =>
        this.getPersonImages(person).some(image => this.imageMatchesRequirement(image, requirement))));
  }

  created() {
    if (!this.prefill || this.prefillUnavailable || !this.product.imageRequirements.length || this.isSet || this.needsBatchSelection) return;
    const persons = this.allPersons.filter(person => this.prefill.personCodes.includes(person.code));
    const imageIds = new Set(persons.flatMap(person => person.imageIds));
    const images = this.$store.getters.imageSelection.images.filter(image => imageIds.has(image.uniqueId));
    this.selectedImages = mapConfigurationPrefill(this.prefill, [{requirement: this.product.imageRequirements[0],
      productId: this.product.id, productFilter: this.product.imageFilter, productMedium: this.product.medium}], images, true)[0];
  }

  get isSet(): boolean {
    // Use productType instead of the requirement count so that a SET with a single
    // sub-product (or a single ProductContent with amount=1) also goes through the
    // SetConfigurator. Otherwise SingleConfigurator would send the SET's id as the
    // ConfiguredImage.productId, which is semantically wrong and breaks the admin
    // order view and print mapping (see OrderMapper.mapOrderItemToConfigurationDto).
    return this.product.productType === ProductTypeDto.SET;
  }

  get needsBatchSelection(): boolean {
    return this.product.imageRequirements[0]?.type === TypeEnum.ALLOFPERSON;
  }

  get availableFilters() {
    return Object.keys(ImageFilterDto).map(id => {
      return {
        id: id,
        name: this.$t(`FILTER_NAME_${id}`)
      }
    });
  }

  onAddedToBasket(items): void {
    this.$emit("addedToBasket", items);
  }

  onError(error: string): void {
    this.$emit("error", error);
  }

}

</script>
<style lang="scss" scoped>

</style>
<style lang="scss">
@use "../../stylesheets/Variables.scss" as *;

.productConfigurator {
  max-height: 100%;

  .title {
    display: block;
    color: $shop-black;
    text-align: center;
    margin-bottom: 15px;
  }

  p {
    text-align: center;
  }

}
</style>
